import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { helperService } from '../../../services/helperService';
import { realtimeService } from '../../../services/realtimeService';

const CalendarView = ({ selectedHelper, onSlotSelect, selectedSlot }) => {
  const [currentWeek, setCurrentWeek] = useState(0);
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'day'
  const [availabilityData, setAvailabilityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch helper availability from Supabase
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!selectedHelper?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const { data, error: fetchError } = await helperService?.getHelperAvailability(selectedHelper?.id);
        
        if (fetchError) {
          setError(fetchError?.message || 'Failed to load availability');
          setAvailabilityData([]);
        } else {
          setAvailabilityData(data || []);
        }
      } catch (err) {
        setError(err?.message || 'An error occurred');
        setAvailabilityData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();

    // Subscribe to real-time availability changes
    const subscription = realtimeService?.subscribeToHelperAvailability((payload) => {
      const { eventType, new: newRecord, old: oldRecord } = payload;

      // Only update if it's for the current helper
      const relevantHelperId = newRecord?.helper_id || oldRecord?.helper_id;
      if (relevantHelperId === selectedHelper?.id) {
        fetchAvailability();
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [selectedHelper?.id]);

  const generateWeekDates = (weekOffset) => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek?.setDate(today?.getDate() - today?.getDay() + (weekOffset * 7));
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date?.setDate(startOfWeek?.getDate() + i);
      dates?.push(date);
    }
    return dates;
  };

  const weekDates = generateWeekDates(currentWeek);

  // Convert database availability to time slots format
  const getSlotsByDate = (date) => {
    const dayOfWeek = date?.getDay();
    
    // Filter availability for this day of week
    const daySlots = availabilityData?.filter(slot => slot?.dayOfWeek === dayOfWeek && slot?.isAvailable);
    
    // Convert to display format
    return daySlots?.map(slot => {
      const timeSlot = slot?.timeSlot; // Format: "HH:MM:SS"
      const [hours, minutes] = timeSlot?.split(':') || [];
      const hour = parseInt(hours, 10);
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const displayTime = `${displayHour?.toString()?.padStart(2, '0')}:${minutes} ${period}`;

      return {
        id: slot?.id,
        time: displayTime,
        rawTime: timeSlot,
        available: true,
        type: slot?.sessionType,
        duration: slot?.duration
      };
    }) || [];
  };

  // Get all unique time slots across all days for display
  const getAllTimeSlots = () => {
    const uniqueTimes = new Set();
    
    weekDates?.forEach(date => {
      const slots = getSlotsByDate(date);
      slots?.forEach(slot => uniqueTimes?.add(slot?.time));
    });

    return Array?.from(uniqueTimes)?.sort((a, b) => {
      // Sort by time
      const timeA = a?.split(' ')?.[0]?.split(':');
      const timeB = b?.split(' ')?.[0]?.split(':');
      const hourA = parseInt(timeA?.[0], 10) + (a?.includes('PM') && timeA?.[0] !== '12' ? 12 : 0);
      const hourB = parseInt(timeB?.[0], 10) + (b?.includes('PM') && timeB?.[0] !== '12' ? 12 : 0);
      return hourA - hourB;
    });
  };

  const timeSlots = getAllTimeSlots();

  const handlePreviousWeek = () => {
    setCurrentWeek(prev => prev - 1);
  };

  const handleNextWeek = () => {
    setCurrentWeek(prev => prev + 1);
  };

  const handleSlotClick = (date, slot) => {
    if (!slot?.available) return;
    
    const slotData = {
      date: date?.toISOString()?.split('T')?.[0],
      time: slot?.time,
      type: slot?.type,
      duration: slot?.duration,
      helper: selectedHelper
    };
    onSlotSelect(slotData);
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDayName = (date) => {
    return date?.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const isToday = (date) => {
    const today = new Date();
    return date?.toDateString() === today?.toDateString();
  };

  const getSessionTypeIcon = (type) => {
    switch (type) {
      case 'video': return 'Video';
      case 'audio': return 'Phone';
      case 'phone': return 'PhoneCall';
      case 'in-person': return 'MapPin';
      default: return 'Calendar';
    }
  };

  const getSessionTypeColor = (type) => {
    switch (type) {
      case 'video': return 'text-primary';
      case 'audio': return 'text-secondary';
      case 'phone': return 'text-accent';
      case 'in-person': return 'text-purple-600';
      default: return 'text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading availability...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center py-12">
          <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
          <p className="text-error mb-2">{error}</p>
          <p className="text-sm text-muted-foreground">Please try again later</p>
        </div>
      </div>
    );
  }

  if (availabilityData?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center py-12">
          <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-foreground font-medium mb-2">No Availability</p>
          <p className="text-sm text-muted-foreground">This helper has not set up their availability yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold text-foreground">Available Time Slots</h2>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'week' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('week')}
            >
              Week
            </Button>
            <Button
              variant={viewMode === 'day' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('day')}
            >
              Day
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            iconName="ChevronLeft"
            onClick={handlePreviousWeek}
            disabled={currentWeek === 0}
          />
          <span className="text-sm font-medium text-foreground min-w-[200px] text-center">
            {formatDate(weekDates?.[0])} - {formatDate(weekDates?.[6])}
          </span>
          <Button
            variant="outline"
            size="icon"
            iconName="ChevronRight"
            onClick={handleNextWeek}
          />
        </div>
      </div>
      <div className="mb-4 flex items-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <Icon name="Video" size={16} className="text-primary" />
          <span className="text-muted-foreground">Video Session</span>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="Phone" size={16} className="text-secondary" />
          <span className="text-muted-foreground">Audio Session</span>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="PhoneCall" size={16} className="text-accent" />
          <span className="text-muted-foreground">Phone Session</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[800px]">
          <div className="grid grid-cols-8 gap-2 mb-2">
            <div className="text-sm font-medium text-muted-foreground"></div>
            {weekDates?.map((date, index) => (
              <div
                key={index}
                className={`text-center p-3 rounded-lg ${
                  isToday(date) ? 'bg-primary/10' : 'bg-muted'
                }`}
              >
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {formatDayName(date)}
                </div>
                <div className={`text-sm font-semibold ${
                  isToday(date) ? 'text-primary' : 'text-foreground'
                }`}>
                  {formatDate(date)}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-1">
            {timeSlots?.map((slot, slotIndex) => (
              <div key={slotIndex} className="grid grid-cols-8 gap-2">
                <div className="flex items-center text-sm font-medium text-muted-foreground py-2">
                  {slot?.time}
                </div>
                {weekDates?.map((date, dateIndex) => {
                  const dateSlots = getSlotsByDate(date);
                  const isSlotAvailable = dateSlots?.some(s => s?.time === slot?.time && s?.available);
                  const slotData = dateSlots?.find(s => s?.time === slot?.time);
                  const isSelected = selectedSlot?.date === date?.toISOString()?.split('T')?.[0] && 
                                   selectedSlot?.time === slot?.time;

                  return (
                    <button
                      key={dateIndex}
                      onClick={() => slotData && handleSlotClick(date, slotData)}
                      disabled={!isSlotAvailable}
                      className={`p-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                        isSelected
                          ? 'bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2'
                          : isSlotAvailable
                          ? 'bg-background border border-border hover:border-primary hover:bg-primary/5 cursor-pointer' :'bg-muted text-muted-foreground cursor-not-allowed opacity-50'
                      }`}
                    >
                      {isSlotAvailable && slotData && (
                        <div className="flex flex-col items-center gap-1">
                          <Icon 
                            name={getSessionTypeIcon(slotData?.type)} 
                            size={14} 
                            className={isSelected ? 'text-primary-foreground' : getSessionTypeColor(slotData?.type)}
                          />
                          <span>{slotData?.duration}m</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-foreground font-medium mb-1">Booking Guidelines</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Sessions can be booked up to 4 weeks in advance</li>
              <li>• Cancellations must be made at least 24 hours before the session</li>
              <li>• You will receive email and SMS confirmations after booking</li>
              <li>• Emergency rescheduling options are available if needed</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;